import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Layers, LogIn } from 'lucide-react';
import type { AppDispatch, RootState } from '@/store';
import { login, clearError } from '@/store/slices/authSlice';
import {
  GlassCard,
  GlassInput,
  GlassButton,
  GlassAlert,
  GlassLoadingSpinner,
  SectionReveal,
} from '@/design-system';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const dispatch = useDispatch<AppDispatch>();
  const { user, isLoading, error } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    // Clear any previous errors when component mounts
    dispatch(clearError());
  }, [dispatch]);

  // Redirect if already logged in
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const isEmailValid = /\S+@\S+\.\S+/.test(email.trim());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isEmailValid) {
      dispatch(login(email.trim()));
    }
  };

  return (
    <div className="min-h-screen glass-page-bg flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <SectionReveal>
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-accent-cyan to-accent-blue flex items-center justify-center mx-auto mb-4 shadow-lg shadow-accent-cyan/30">
              <Layers className="w-8 h-8 text-text-inverse" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">Portfolio Manager</h1>
            <p className="text-muted-foreground mt-2">Enterprise Portfolio Planning Platform</p>
          </div>
        </SectionReveal>

        {/* Login Form */}
        <SectionReveal delay={0.1}>
          <GlassCard padding="lg">
            <form onSubmit={handleSubmit} className="space-y-6">
              <GlassInput
                id="email"
                type="email"
                label="Email Address"
                value={email}
                onChange={(value) => setEmail(value)}
                placeholder="Enter your email"
                required
                autoFocus
                helperText="Demo: Use any valid email format (password not required)"
              />

              {error && (
                <GlassAlert variant="error" message={error} />
              )}

              <GlassButton
                type="submit"
                variant="primary"
                disabled={isLoading || !isEmailValid}
                fullWidth
                leftIcon={isLoading ? undefined : <LogIn className="w-4 h-4" />}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <GlassLoadingSpinner size="sm" />
                    Signing in...
                  </div>
                ) : (
                  'Sign In'
                )}
              </GlassButton>
            </form>
          </GlassCard>
        </SectionReveal>

        {/* Demo Info */}
        <SectionReveal delay={0.2}>
          <div className="mt-8 text-center">
            <p className="text-xs text-muted-foreground/60">
              This is a demo application. Use any email address to sign in.
            </p>
          </div>
        </SectionReveal>
      </div>
    </div>
  );
};

export default LoginPage;
